/** biome-ignore-all lint/suspicious/noArrayIndexKey: ignore */
import '@fontsource/poppins';
import axios from 'axios';
import debounce from 'lodash.debounce';

import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TablePagination,
  TextField,
} from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Tab from '@mui/material/Tab';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';

import React, { useEffect, useState } from 'react';

import { toast } from 'react-toastify';

import { DeleteButton, EditButton } from '@src/components/buttons';
import ButtonAction from '@src/components/buttons/action.button';
import AddListingScope, {
  validProfessions,
} from '@src/components/modals/addListingScope';
import EditListingScope from '@src/components/modals/editListingScope';
import { useAuth } from '@src/contexts/authContext';
import { formatDate } from '@src/utils';

const url = `${process.env.REACT_APP_API_URL}/v2/admin/scopes`;

function TabPanel(props) {
  const { children, value, index, style = {} } = props;

  return (
    <div
      role='tabpanel'
      hidden={value !== index}
      id={`master-data-listing-scopes-profession-tabpanel-${index}`}
      aria-labelledby={`master-data-listing-scopes-profession-tab-${index}`}
      style={style}
    >
      {value === index && (
        <Box sx={{ p: 1 }}>
          <Typography>{children}</Typography>
        </Box>
      )}
    </div>
  );
}

export default function ListingScopes() {
  const { auth } = useAuth();
  const [tabIndex, setTabIndex] = React.useState(() => {
    const savedTab = localStorage.getItem('MasterData.ListingScope.Tab');
    return savedTab !== null ? Number(savedTab) : 0;
  });
  const [loading, setLoading] = useState();
  const [scopes, setScopes] = useState([]);
  const [deleteStatus, setDeleteStatus] = useState();
  const [deleteId, setDeleteId] = useState(null);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(25);

  const fetchData = React.useCallback(() => {
    setLoading(true);
    axios
      .get(`${url}?with_deleted=1`, {
        headers: {
          Authorization: `Bearer ${auth.idToken}`,
        },
      })
      .then((response) => {
        if (Array.isArray(response.data.data)) {
          setScopes(response.data.data);
        } else {
          setScopes([]);
        }
        setLoading(false);
      })
      .catch((error) => {
        console.error(error);
        setLoading(false);
      });
  }, [auth?.idToken]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: ignore
  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deleteStatus, fetchData]);

  const handleChangeTab = async (_ev, newValue) => {
    setTabIndex(newValue);
    localStorage.setItem('MasterData.ListingScope.Tab', newValue);
  };

  const handleChangePage = async (_event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = async (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const handleDelete = async (id) => {
    try {
      const response = await axios.delete(`${url}/items/${id}/delete`, {
        headers: {
          Authorization: `Bearer ${auth.idToken}`,
        },
      });

      if (response.status === 200) {
        setDeleteStatus('Delete successful');
        toast.success('Item deleted successfully', {
          position: 'top-center',
          autoClose: 2000,
        });
      } else {
        console.error('Delete operation failed');
      }
    } catch (error) {
      console.error(error);
    }
  };

  const showDeleteConfirmationDialog = async (id) => {
    setDeleteId(id);
    setShowDeleteConfirmation(true);
  };

  const handleConfirmationDialogClose = async () => {
    setShowDeleteConfirmation(false);
  };

  const handleConfirmationDialogConfirm = async () => {
    handleDelete(deleteId);
    setShowDeleteConfirmation(false);
  };

  // use item id as dialog `open` (bool)
  const [openAddModal, setOpenAddModal] = useState(null);
  const showAddModal = () => {
    setOpenAddModal(true);
  };
  // use item id as dialog `open` (bool)
  const [openEditModal, setOpenEditModal] = useState(null);
  const showEditModal = (itemId) => {
    setOpenEditModal(itemId);
  };

  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');

  const debouncedSetQuery = React.useCallback(
    debounce((value) => {
      setDebouncedQuery(value);
    }, 370),
    [],
  );

  const handleSearch = (event) => {
    const value = event?.target?.value?.trim() ?? '';
    setSearchQuery(value);
    debouncedSetQuery(value);
  };

  const filteredList = React.useMemo(
    () =>
      debouncedQuery
        ? scopes?.map?.((data) => {
            return {
              ...data,
              scopes:
                data?.scopes?.filter?.(
                  (row) =>
                    row?.label
                      ?.toLowerCase?.()
                      .includes(debouncedQuery.toLowerCase()) ||
                    row?.value
                      ?.toLowerCase?.()
                      .includes(debouncedQuery.toLowerCase()) ||
                    false,
                ) || [],
            };
          })
        : scopes,
    [scopes, debouncedQuery],
  );

  return (
    <div>
      <div className='py-4'>
        <Box sx={{ width: '100%' }} className='flex justify-between px-3 py-2'>
          <TextField
            fullWidth
            size='medium'
            variant='outlined'
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                backgroundColor: 'white',
                height: '50px',
              },
              mt: '5px',
              width: '250px',
            }}
            value={searchQuery}
            onChange={handleSearch}
            placeholder='Search by name of item'
          />
          <ButtonAction
            onNavigate={() => {
              showAddModal();
            }}
            label='Add New Item'
            sx={{
              display: 'inline-flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          />
        </Box>
        {loading ? (
          <div className='font-poppins py-6 px-8 text-gray-500'>Loading...</div>
        ) : (
          <>
            <Box
              sx={{ borderBottom: 1, borderColor: 'divider' }}
              className=' mt-2 border-gray-50'
            >
              <Tabs
                value={tabIndex}
                indicatorColor='white'
                onChange={(ev, newValue) => {
                  handleChangeTab(ev, newValue);
                }}
                aria-label='listing scopes'
                sx={{ px: '12px' }}
              >
                {scopes
                  .map((scope, idx) => [
                    <Tab
                      key={`master-data-listing-scopes-profession-tab-item-${idx}`}
                      sx={{ textTransform: 'none' }}
                      label={scope?.profession || 'Unknown'}
                      className={`rounded-md border border-gray-300 font-bold capitalize text-cyan-600 ${
                        tabIndex === idx * 2
                          ? 'bg-cyan-600 text-white'
                          : 'bg-white'
                      }`}
                      id={`master-data-listing-scopes-profession-tab-${idx}`}
                      aria-controls={`master-data-listing-scopes-profession-tabpanel-${idx}`}
                    />,
                    // additional box as separator
                    idx < scopes.length - 1 ? (
                      <Box
                        key={`master-data-listing-scopes-profession-tab-separator-${idx}`}
                        sx={{ width: '10px' }}
                      />
                    ) : null,
                  ])
                  .flat()}
              </Tabs>
            </Box>
            {filteredList.map((scopeData, idx) => (
              <TabPanel
                value={tabIndex}
                // with idx * 2 to compensate the additional box
                index={idx * 2}
                key={`master-data-listing-scopes-profession-scope-items-tab-panel-item-${
                  scopeData?._id || idx
                }`}
              >
                <div className='flex flex-col items-start py-4'>
                  <TableContainer
                    className='rounded-xl'
                    component={Paper}
                    style={{
                      width: '100%',
                      paddingBottom: '12px',
                      paddingRight: '12px',
                      backgroundColor: '#ffffff',
                      borderColor: 'ffffff',
                    }}
                  >
                    <Table
                      sx={{
                        minWidth: 350,
                        backgroundColor: '#ffffff',
                        borderColor: 'ffffff',
                      }}
                      aria-label='listing scope items table'
                    >
                      <TableHead>
                        <TableRow>
                          <TableCell
                            className='text-xs font-bold text-gray-400'
                            style={{
                              width: '350px',
                              border: 'gray',
                              paddingLeft: 30,
                            }}
                          >
                            Value
                          </TableCell>

                          <TableCell
                            className='text-xs font-bold text-gray-400'
                            style={{
                              width: '350px',
                              border: 'gray',
                              paddingLeft: 30,
                            }}
                          >
                            Label/Title
                          </TableCell>

                          <TableCell
                            className='text-xs font-bold text-gray-400'
                            style={{
                              width: '350px',
                              border: 'gray',
                              paddingLeft: 30,
                            }}
                          >
                            Description
                          </TableCell>

                          <TableCell
                            className='text-xs font-bold text-gray-400'
                            style={{
                              padding: 4,
                              paddingLeft: 30,
                              width: '100px',
                              border: 'gray',
                            }}
                          >
                            List No
                          </TableCell>

                          <TableCell
                            className='text-xs font-bold text-gray-400'
                            style={{
                              padding: 4,
                              width: '100px',
                              border: 'gray',
                            }}
                          >
                            Is Deleted
                          </TableCell>

                          <TableCell
                            className='text-xs font-bold text-gray-400'
                            style={{
                              padding: 4,
                              width: '150px',
                              border: 'gray',
                            }}
                          >
                            Updated at
                          </TableCell>

                          <TableCell
                            className='text-xs font-bold text-gray-400'
                            style={{
                              padding: 4,
                              width: '100px',
                              border: 'gray',
                            }}
                          >
                            Actions
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {Array.isArray(scopeData?.scopes) &&
                        scopeData?.scopes.length > 0 ? (
                          scopeData?.scopes
                            .slice(
                              page * rowsPerPage,
                              page * rowsPerPage + rowsPerPage,
                            )
                            .map((row, rowIdx) => (
                              <TableRow
                                key={row?._id || rowIdx}
                                hover
                                tabIndex={-1}
                                sx={{
                                  '&:last-child td, &:last-child th': {
                                    border: 0,
                                  },
                                }}
                              >
                                <TableCell
                                  component='th'
                                  scope='row'
                                  style={{ margin: 2, paddingLeft: 30 }}
                                  className='text-sm font-bold text-gray-500'
                                >
                                  {row?.value ?? '-'}
                                </TableCell>

                                <TableCell
                                  component='th'
                                  scope='row'
                                  style={{ margin: 2, paddingLeft: 30 }}
                                  className='text-sm font-bold text-gray-500'
                                >
                                  {row?.help?.title ?? row?.label ?? '-'}
                                </TableCell>

                                <TableCell
                                  component='th'
                                  scope='row'
                                  style={{ margin: 2, paddingLeft: 30 }}
                                  className='text-sm font-bold text-gray-500'
                                >
                                  {row?.help?.description ?? '-'}
                                </TableCell>

                                <TableCell
                                  component='th'
                                  scope='row'
                                  style={{ margin: 2, paddingLeft: 30 }}
                                  className='text-sm font-bold text-gray-500'
                                >
                                  {row?.sort ?? '-'}
                                </TableCell>

                                <TableCell
                                  className='text-sm font-bold text-gray-500'
                                  style={{ padding: 4 }}
                                >
                                  {row?.is_deleted ? 'true' : 'false'}
                                </TableCell>

                                <TableCell
                                  className=' text-sm font-bold text-gray-500'
                                  style={{ padding: 4, margin: 2 }}
                                >
                                  {row?.updated_at &&
                                  !Number.isNaN(
                                    new Date(row.updated_at).getTime(),
                                  )
                                    ? formatDate(row.updated_at)
                                    : '-'}
                                </TableCell>

                                <TableCell style={{ padding: 4, margin: 2 }}>
                                  <div className='flex justify-start '>
                                    <EditButton
                                      disabled={
                                        !validProfessions.includes(
                                          scopeData.profession,
                                        )
                                      }
                                      onClick={() => showEditModal(row?._id)}
                                    />
                                    &nbsp; &nbsp;
                                    <DeleteButton
                                      disabled={
                                        row?.is_deleted ||
                                        !validProfessions.includes(
                                          scopeData.profession,
                                        )
                                      }
                                      onClick={() =>
                                        showDeleteConfirmationDialog(row?._id)
                                      }
                                    />
                                  </div>
                                  {showDeleteConfirmation && (
                                    <Dialog
                                      open={showDeleteConfirmation}
                                      onClose={handleConfirmationDialogClose}
                                    >
                                      <DialogTitle>Confirm Delete</DialogTitle>
                                      <DialogContent>
                                        <DialogContentText>
                                          Are you sure you want to delete this
                                          item?
                                        </DialogContentText>
                                      </DialogContent>
                                      <DialogActions>
                                        <Button
                                          onClick={
                                            handleConfirmationDialogClose
                                          }
                                          color='primary'
                                        >
                                          Cancel
                                        </Button>
                                        <Button
                                          disabled={
                                            !validProfessions.includes(
                                              scopeData.profession,
                                            )
                                          }
                                          onClick={
                                            handleConfirmationDialogConfirm
                                          }
                                          color='error'
                                        >
                                          Delete
                                        </Button>
                                      </DialogActions>
                                    </Dialog>
                                  )}
                                </TableCell>
                              </TableRow>
                            ))
                        ) : (
                          <TableRow>
                            <TableCell
                              colSpan={4}
                              className='text-center text-gray-500'
                            >
                              No data available
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                    <Box
                      className='flex justify-end py-3'
                      sx={{ width: '100%', fontFamily: 'Poppins' }}
                    >
                      <TablePagination
                        rowsPerPageOptions={[25, 50, 100]}
                        component='div'
                        count={scopeData?.scopes?.length || 0}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                      />
                    </Box>
                  </TableContainer>
                </div>
              </TabPanel>
            ))}
          </>
        )}
      </div>

      <AddListingScope
        show={showAddModal}
        open={openAddModal}
        setOpen={setOpenAddModal}
        fetchData={fetchData}
      />

      <EditListingScope
        show={showEditModal}
        open={openEditModal}
        setOpen={setOpenEditModal}
        fetchData={fetchData}
      />
      <br />
      <br />
      <br />
    </div>
  );
}
